import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { haafPaymentKeys } from "./keys";
import {
  createHaafPayment,
  deleteHaafPayment,
  getAllHaafPayments,
  updateHaafPayment,
  verifyHaafPayment,
  unverifyHaafPayment,
  getChapterHaafPayments,
  getHaafPaymentsByBatch,
  getHaafPaymentStats,
  getHaafPaymentBatchStats,
  getHaafPaymentChapterStats,
} from "./api";
import { toast } from "sonner";

export const useHaafPayments = () => {
  return useQuery({
    queryKey: haafPaymentKeys.list(),
    queryFn: getAllHaafPayments,
  });
};

export const useCreateHaafPayment = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createHaafPayment,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: haafPaymentKeys.list() });
      toast.success("HAAF Payment created successfully");
    },
    onError: (error) => {
      toast.error(
        error.response?.data?.message || "Failed to create HAAF payment"
      );
    },
  });
};

export const useUpdateHaafPayment = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updateHaafPayment,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: haafPaymentKeys.list() });
      toast.success("HAAF Payment updated successfully");
    },
    onError: (error) => {
      toast.error(
        error.response?.data?.message || "Failed to update HAAF payment"
      );
    },
  });
};

export const useDeleteHaafPayment = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: deleteHaafPayment,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: haafPaymentKeys.list() });
      toast.success("HAAF Payment deleted successfully");
    },
    onError: (error) => {
      toast.error(
        error.response?.data?.message || "Failed to delete HAAF payment"
      );
    },
  });
};

export const useVerifyHaafPayment = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: verifyHaafPayment,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: haafPaymentKeys.list() });
      toast.success("HAAF Payment verified successfully");
    },
    onError: (error) => {
      toast.error(
        error.response?.data?.message || "Failed to verify HAAF payment"
      );
    },
  });
};

export const useUnverifyHaafPayment = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: unverifyHaafPayment,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: haafPaymentKeys.list() });
      toast.success("HAAF Payment unverified successfully");
    },
    onError: (error) => {
      toast.error(
        error.response?.data?.message || "Failed to unverify HAAF payment"
      );
    },
  });
};

export const useChapterHaafPayments = () => {
  return useQuery({
    queryKey: haafPaymentKeys.chapterList(),
    queryFn: getChapterHaafPayments,
  });
};

export const useHaafPaymentsByBatch = (batch) => {
  return useQuery({
    queryKey: haafPaymentKeys.batchList(batch),
    queryFn: () => getHaafPaymentsByBatch(batch),
    enabled: !!batch, // Only run query if batch is provided
  });
};

export const useHaafPaymentStats = () => {
  return useQuery({
    queryKey: haafPaymentKeys.stats(),
    queryFn: getHaafPaymentStats,
  });
};

export const useHaafPaymentBatchStats = () => {
  return useQuery({
    queryKey: haafPaymentKeys.batchStats(),
    queryFn: getHaafPaymentBatchStats,
  });
};

export const useHaafPaymentChapterStats = () => {
  return useQuery({
    queryKey: haafPaymentKeys.chapterStats(),
    queryFn: getHaafPaymentChapterStats,
  });
};
